import React, { useCallback, useEffect, useState, useRef, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapContainer, TileLayer, Polygon, Marker, Popup, Tooltip, useMap, useMapEvents } from 'react-leaflet';
import { Truck, Info, Search, Loader2, Navigation } from 'lucide-react';
import L from 'leaflet';
// Fix for default marker icon in react-leaflet
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png'
});
import { DELIVERY_CENTER, DELIVERY_ZONES as zones, getZoneForPosition } from '../data/deliveryZones';
import { Skeleton } from '../components/Skeleton';
const CENTER: [number, number] = DELIVERY_CENTER;
const MAP_CENTER: [number, number] = DELIVERY_CENTER;
// Haversine formula to calculate distance in miles
function getDistanceMiles(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // Earth radius in miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}
// Component to handle map clicks outside zones
function MapClickHandler({
  center
}: {
  center: [number, number];
}) {
  const map = useMapEvents({
    click(e) {
      const zoneInfo = getZoneForPosition(e.latlng.lat, e.latlng.lng);
      if (zoneInfo.zone === 0) {
        L.popup({
          className: 'zone-popup-dark'
        }).setLatLng(e.latlng).setContent('<div style="text-align:center;padding:4px 8px;"><p style="font-weight:bold;color:#ffffff;margin:0 0 4px;">Outside Delivery Zones</p><p style="margin:0;font-size:14px;"><a href="tel:206-619-3742" style="color:#F87431;font-weight:bold;text-decoration:none;">📞 Call for Quote</a></p><p style="margin:4px 0 0;font-size:12px;color:#bbbbbb;">(206) 619-3742</p></div>').openOn(map);
      }
    }
  });
  return null;
}
// Component to fly the map to a location
function FlyTo({
  position
}: {
  position: [number, number] | null;
}) {
  const map = useMap();
  useEffect(() => {
    if (position) {
      map.flyTo(position, 11, {
        duration: 1.5
      });
    }
  }, [position, map]);
  return null;
}
// Ping data for random purchases
const PING_MATERIALS: {
  name: string;
  maxYards: number;
}[] = [{
  name: '5/8" Minus Gravel',
  maxYards: 12
}, {
  name: 'Pea Gravel',
  maxYards: 12
}, {
  name: '7/8" Drain Rock',
  maxYards: 12
}, {
  name: '3-Way Topsoil',
  maxYards: 15
}, {
  name: 'Medium Bark',
  maxYards: 15
}, {
  name: 'Coarse Bark',
  maxYards: 15
}, {
  name: '1.25" Minus',
  maxYards: 12
}, {
  name: 'Road Base',
  maxYards: 12
}, {
  name: 'Builders Sand',
  maxYards: 12
}, {
  name: 'Fill Dirt',
  maxYards: 15
}, {
  name: '2-4" Spalls',
  maxYards: 12
}];
const YARD_OPTIONS = [12, 15];
function getRandomQuantity(maxYards: number): string {
  const valid = YARD_OPTIONS.filter(y => y <= maxYards);
  return `${valid[Math.floor(Math.random() * valid.length)]} yards`;
}
function randomDateRecent(): string {
  const daysAgo = Math.floor(Math.random() * 7);
  if (daysAgo === 0) return 'Today';
  if (daysAgo === 1) return 'Yesterday';
  return `${daysAgo} days ago`;
}
type PingData = {
  id: number;
  latLng: [number, number];
  material: string;
  quantity: string;
  date: string;
  createdAt: number;
};
// Radar Overlay Component
function RadarOverlay({
  center,
  isVisible
}: {
  center: [number, number];
  isVisible: boolean;
}) {
  const map = useMap();
  const [position, setPosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [pings, setPings] = useState<PingData[]>([]);
  const pingIdRef = useRef(0);
  useEffect(() => {
    if (!isVisible) return;
    const updatePosition = () => {
      const point = map.latLngToContainerPoint(center);
      setPosition({
        x: point.x,
        y: point.y
      });
    };
    updatePosition();
    map.on('move', updatePosition);
    map.on('zoom', updatePosition);
    return () => {
      map.off('move', updatePosition);
      map.off('zoom', updatePosition);
    };
  }, [map, center, isVisible]);
  const [size, setSize] = useState(120);
  useEffect(() => {
    if (!isVisible) return;
    const updateSize = () => {
      const centerPoint = map.latLngToContainerPoint(center);
      // 40 miles radius — reaches the outer edges of Zone 5
      const edgeLat = center[0] + 40 * 1.60934 / 111.32;
      const edgePoint = map.latLngToContainerPoint([edgeLat, center[1]]);
      const pixelRadius = Math.abs(centerPoint.y - edgePoint.y);
      setSize(pixelRadius * 2);
    };
    updateSize();
    map.on('zoom', updateSize);
    map.on('zoomend', updateSize);
    return () => {
      map.off('zoom', updateSize);
      map.off('zoomend', updateSize);
    };
  }, [map, center, isVisible]);
  // Track radar angle and spawn pings when the sweep line crosses a queued position
  const radarStartRef = useRef(Date.now());
  const RADAR_DURATION = 13500; // matches CSS animation duration in ms
  const nextPingAngleRef = useRef<number>(Math.random() * 360);
  const lastSpawnAngleRef = useRef<number>(-1);
  useEffect(() => {
    if (!isVisible) return;
    radarStartRef.current = Date.now();
    const zoneRanges: [number, number][] = [[0, 8], [8, 14], [14, 22], [22, 30], [30, 38]];
    // Seed 3 beyond-zone-5 deliveries: 2 north, 1 south
    const beyondPings: PingData[] = [{
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 42 + Math.random() * 4;
        const angleRad = (350 + Math.random() * 20) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: '5/8" Minus Gravel',
      quantity: '12 yards',
      date: 'Yesterday',
      createdAt: Date.now() - 500
    }, {
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 43 + Math.random() * 3;
        const angleRad = (10 + Math.random() * 30) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: '3-Way Topsoil',
      quantity: '15 yards',
      date: '2 days ago',
      createdAt: Date.now() - 800
    }, {
      id: pingIdRef.current++,
      latLng: (() => {
        const dist = 42 + Math.random() * 5;
        const angleRad = (170 + Math.random() * 20) * Math.PI / 180;
        const distKm = dist * 1.60934;
        return [center[0] + distKm / 111.32 * Math.cos(angleRad), center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad)] as [number, number];
      })(),
      material: 'Medium Bark',
      quantity: '15 yards',
      date: 'Today',
      createdAt: Date.now() - 300
    }];
    setPings(beyondPings);
    // Queue the first target angle
    nextPingAngleRef.current = Math.random() * 360;
    lastSpawnAngleRef.current = -1;
    const tick = setInterval(() => {
      const elapsed = (Date.now() - radarStartRef.current) % RADAR_DURATION;
      const currentAngle = elapsed / RADAR_DURATION * 360;
      const target = nextPingAngleRef.current;
      // Check if the radar arm just crossed the target angle
      const prev = lastSpawnAngleRef.current;
      const crossed = prev >= 0 && (prev < target && currentAngle >= target || prev > currentAngle && (prev < target || currentAngle >= target));
      lastSpawnAngleRef.current = currentAngle;
      if (crossed) {
        // Spawn ping at the target angle, random zone
        const zoneRange = zoneRanges[Math.floor(Math.random() * zoneRanges.length)];
        // Use the target angle for the ping position
        const dist = zoneRange[0] + Math.random() * (zoneRange[1] - zoneRange[0]);
        const distKm = dist * 1.60934;
        const angleRad = target * Math.PI / 180;
        const lat = center[0] + distKm / 111.32 * Math.cos(angleRad);
        const lng = center[1] + distKm / (111.32 * Math.cos(center[0] * (Math.PI / 180))) * Math.sin(angleRad);
        const mat = PING_MATERIALS[Math.floor(Math.random() * PING_MATERIALS.length)];
        const newPing: PingData = {
          id: pingIdRef.current++,
          latLng: [lat, lng],
          material: mat.name,
          quantity: getRandomQuantity(mat.maxYards),
          date: randomDateRecent(),
          createdAt: Date.now()
        };
        setPings(prev => [...prev.slice(-9), newPing]);
        // Queue next ping at a random angle offset (60-240 degrees ahead)
        nextPingAngleRef.current = (target + 60 + Math.random() * 180) % 360;
      }
    }, 100);
    return () => clearInterval(tick);
  }, [center, isVisible]);
  // Remove expired pings (older than 5 seconds), but keep seeded beyond-zone pings (id < 3)
  useEffect(() => {
    if (pings.length === 0) return;
    const timer = setInterval(() => {
      setPings(prev => prev.filter(p => p.id < 3 || Date.now() - p.createdAt < 5000));
    }, 500);
    return () => clearInterval(timer);
  }, [pings.length]);
  if (!isVisible || !position) return null;
  return <>
      {/* Radar sweep */}
      <div className="absolute pointer-events-none z-[400]" style={{
      left: position.x - size / 2,
      top: position.y - size / 2,
      width: size,
      height: size
    }} data-id="element-1602">
        <div className="relative w-full h-full rounded-[50%] bg-brand-orange/[0.02]" data-id="element-1603">
          <div className="radar-sweep" data-id="element-1604" />
          <div className="absolute top-1/2 left-1/2 w-2 h-2 -mt-1 -ml-1 bg-brand-orange/50 rounded-full shadow-[0_0_4px_rgba(5,137,77,0.4)]" data-id="element-1605" />
        </div>
      </div>

      {/* Purchase pings */}
      {pings.map(ping => {
      const point = map.latLngToContainerPoint(ping.latLng);
      const age = Date.now() - ping.createdAt;
      const opacity = age < 1000 ? age / 1000 : age > 3500 ? 1 - (age - 3500) / 1500 : 1;
      return <div key={ping.id} className="absolute pointer-events-none z-[450]" style={{
        left: point.x,
        top: point.y,
        transform: `translate(-50%, -100%) scale(${age < 1000 ? 0.8 + 0.2 * (age / 1000) : 1})`,
        opacity: Math.max(0, opacity),
        transition: 'opacity 0.5s ease, transform 0.5s ease'
      }} data-id="element-1606">
            {/* Ping dot */}
            <div className="absolute bottom-0 left-1/2 -translate-x-1/2 translate-y-1/2 w-2 h-2 bg-brand-orange rounded-full shadow-[0_0_6px_rgba(5,137,77,0.6)]" data-id="element-1607" />
            {/* Info card */}
            <div className="mb-2 bg-black/70 backdrop-blur-md border border-white/10 rounded-lg px-3 py-2 whitespace-nowrap shadow-xl" data-id="element-1608">
              <p className="text-white text-xs font-semibold" data-id="element-1609">
                {ping.quantity} — {ping.material}
              </p>
              <p className="text-[10px]" style={{
            color: '#808080'
          }} data-id="element-1610">
                Purchased {ping.date}
              </p>
            </div>
          </div>;
    })}
    </>;
}
// Custom pulsating red marker for user location
const pulsatingIcon = L.divIcon({
  className: 'custom-div-icon',
  html: '<div class="pulsating-pin"><div class="pulsating-pin-dot"></div></div>',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
  popupAnchor: [0, -15]
});
export function DeliveryZones() {
  const [address, setAddress] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [userPosition, setUserPosition] = useState<[number, number] | null>(null);
  const [searchResult, setSearchResult] = useState<{
    zone: number;
    price: string;
    color: string;
    distance: number;
    displayName: string;
  } | null>(null);
  const [searchError, setSearchError] = useState('');
  const hasAutoSearched = useRef(false);
  const [mapLoaded, setMapLoaded] = useState(false);
  const handleSearch = useCallback(async () => {
    if (!address.trim()) return;
    setIsSearching(true);
    setSearchError('');
    setSearchResult(null);
    setUserPosition(null);
    try {
      const query = encodeURIComponent(address + ', WA');
      const res = await fetch(`https://nominatim.openstreetmap.org/search?format=json&q=${query}&limit=1&countrycodes=us`);
      const data = await res.json();
      if (!data || data.length === 0) {
        setSearchError('Address not found. Please try a more specific address.');
        setIsSearching(false);
        return;
      }
      const lat = parseFloat(data[0].lat);
      const lon = parseFloat(data[0].lon);
      const distance = getDistanceMiles(CENTER[0], CENTER[1], lat, lon);
      const zoneInfo = getZoneForPosition(lat, lon);
      setUserPosition([lat, lon]);
      setSearchResult({
        ...zoneInfo,
        distance: Math.round(distance * 10) / 10,
        displayName: data[0].display_name
      });
    } catch {
      setSearchError('Something went wrong. Please try again.');
    }
    setIsSearching(false);
  }, [address]);
  // Auto-search if address is passed via URL query param
  useEffect(() => {
    if (hasAutoSearched.current) return;
    // With HashRouter, query params are inside the hash: /#/delivery-zones?address=...
    const hash = window.location.hash; // e.g. "#/delivery-zones?address=123+Main"
    const qIndex = hash.indexOf('?');
    const params = qIndex >= 0 ? new URLSearchParams(hash.slice(qIndex)) : new URLSearchParams();
    const urlAddress = params.get('address');
    if (urlAddress) {
      setAddress(urlAddress);
      hasAutoSearched.current = true;
    }
  }, []);
  // Trigger search when address is set from URL param
  useEffect(() => {
    if (hasAutoSearched.current && address && !searchResult && !isSearching && !searchError) {
      handleSearch();
    }
  }, [address, hasAutoSearched.current, searchResult, isSearching, searchError, handleSearch]);
  return <main className="relative w-full min-h-[calc(100vh-64px)] flex flex-col lg:block bg-[#373737]" data-id="element-1611">
      {/* Single Map Container - Works at all screen sizes */}
      <div className="h-[50vh] lg:h-auto lg:absolute lg:inset-0 z-0 relative" data-id="element-1612">
        {!mapLoaded && <Skeleton className="absolute inset-0 z-10" data-id="element-1613" />}
        <MapContainer center={MAP_CENTER} zoom={10} scrollWheelZoom={true} className="w-full h-full" zoomControl={false} whenReady={() => setMapLoaded(true)} data-id="element-1614">
          <TileLayer attribution="&copy; Google Maps" url="https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}" data-id="element-1615" />
          <FlyTo position={userPosition} data-id="element-1616" />
          <MapClickHandler center={CENTER} data-id="element-1617" />

          <RadarOverlay center={CENTER} isVisible={!userPosition} data-id="element-1618" />

          {[...zones].reverse().map(zone => zone.polygons.map((polygonCoords, idx) => <Polygon key={`${zone.id}-${idx}`} positions={polygonCoords as [number, number][]} pathOptions={{
          stroke: false,
          fillColor: zone.color,
          fillOpacity: 0.06
        }} data-id="element-1619">
                <Popup className="zone-popup-dark" data-id="element-1620">
                  <div className="text-center" data-id="element-1621">
                    <p className="font-bold text-white" data-id="element-1622">Zone {zone.id}</p>
                    <p className="font-bold text-brand-orange mt-1" data-id="element-1623">
                      Delivery: {zone.price}
                    </p>
                  </div>
                </Popup>
              </Polygon>))}

          {userPosition && <Marker position={userPosition} icon={pulsatingIcon} data-id="element-1624">
              {searchResult && <Tooltip permanent direction="right" offset={[12, 0]} className="zone-speech-bubble" data-id="element-1625">
                  <div className="flex items-center gap-2" data-id="element-1626">
                    <span className="font-bold text-white text-sm" data-id="element-1627">
                      {searchResult.zone > 0 ? `Zone ${searchResult.zone}` : 'Outside Zones'}
                    </span>
                    <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-white/15 text-white/90" data-id="element-1628">
                      {searchResult.zone > 0 ? searchResult.price === 'FREE' ? 'Free Delivery' : `Delivery: ${searchResult.price}` : 'Call for Quote'}
                    </span>
                  </div>
                </Tooltip>}
              <Popup data-id="element-1629">
                <div className="text-center" data-id="element-1630">
                  <p className="font-bold text-brand-charcoal" data-id="element-1631">Your Location</p>
                  {searchResult && <p className="text-sm text-stone-600" data-id="element-1632">
                      Zone {searchResult.zone || 'Outside'}
                    </p>}
                </div>
              </Popup>
            </Marker>}
        </MapContainer>
      </div>

      {/* Top Gradient Overlay (Desktop) */}
      <div className="hidden lg:block absolute top-0 left-0 right-0 h-64 bg-gradient-to-b from-[#373737]/90 via-[#373737]/50 to-transparent z-10 pointer-events-none" data-id="element-1633" />

      {/* Floating UI Container */}
      <div className="relative z-20 flex-1 flex flex-col lg:block lg:absolute lg:inset-0 lg:pointer-events-none" data-id="element-1634">
        {/* Header & Search (Top Center) */}
        <div className="w-full max-w-3xl mx-auto px-4 pt-8 lg:pt-16 pointer-events-auto" data-id="element-1635">
          <motion.div initial={{
          opacity: 0,
          y: -20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="text-center mb-8" data-id="element-1636">
            <h1 className="font-heading text-4xl lg:text-5xl font-extrabold text-white mb-3 tracking-tight drop-shadow-lg" data-id="element-1637">
              Delivery <span className="text-brand-orange" data-id="element-1638">Zones</span>
            </h1>
            <p className="text-lg text-stone-300 font-medium drop-shadow-md max-w-xl mx-auto font-sub" data-id="element-1639">
              Enter your address to instantly calculate your delivery zone and
              cost.
            </p>
          </motion.div>

          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          delay: 0.1
        }} data-id="element-1640">
            <form onSubmit={e => {
            e.preventDefault();
            handleSearch();
          }} className="flex flex-col sm:flex-row bg-[#474747]/95 backdrop-blur-md border border-brand-orange shadow-2xl rounded-[5px]" data-id="element-1641">
              <div className="flex-1 relative" data-id="element-1642">
                <Navigation className="absolute left-5 top-1/2 -translate-y-1/2 w-5 h-5 text-stone-400" data-id="element-1643" />
                <input type="text" value={address} onChange={e => setAddress(e.target.value)} placeholder="Enter your delivery address..." autoComplete="street-address" className="w-full pl-14 pr-4 py-4 bg-transparent text-white placeholder-stone-400 focus:outline-none text-lg border-none focus:ring-0 transition-colors" data-id="element-1644" />
              </div>
              <button type="submit" disabled={isSearching || !address.trim()} className="px-8 py-4 bg-brand-button hover:bg-brand-buttonHover disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold transition-all flex items-center justify-center gap-2 shrink-0 border-l border-brand-orange" data-id="element-1645">
                {isSearching ? <Loader2 className="w-5 h-5 animate-spin" data-id="element-1646" /> : <Search className="w-5 h-5" data-id="element-1647" />}
                <span data-id="element-1648">Find Zone</span>
              </button>
            </form>

            {/* Search Result */}
            <AnimatePresence mode="wait" data-id="element-1649">
              {searchError && <motion.div initial={{
              opacity: 0,
              y: -10
            }} animate={{
              opacity: 1,
              y: 0
            }} exit={{
              opacity: 0,
              scale: 0.95
            }} className="mt-4 p-4 bg-red-500/20 backdrop-blur-md border border-red-500/30 rounded-2xl text-red-200 text-center text-sm font-medium" data-id="element-1650">
                  {searchError}
                </motion.div>}
            </AnimatePresence>
          </motion.div>
        </div>

        {/* Legend (Bottom Left Desktop, Flow Mobile) */}
        <div className="w-full lg:fixed lg:top-[143px] lg:left-8 lg:w-auto p-4 lg:p-0 pointer-events-auto lg:z-40" data-id="element-1651">
          <motion.div initial={{
          opacity: 0,
          x: -20
        }} animate={{
          opacity: 1,
          x: 0
        }} transition={{
          delay: 0.3
        }} className="bg-[#474747]/95 backdrop-blur-xl rounded-3xl p-6 border border-white/10 shadow-2xl w-full lg:w-[340px]" data-id="element-1652">
            <h2 className="font-sans text-lg font-bold text-white mb-4 flex items-center" data-id="element-1653">
              <Truck className="w-5 h-5 mr-2 text-brand-orange" data-id="element-1654" />
              Delivery Pricing
            </h2>

            <div className="space-y-2" data-id="element-1655">
              {/* Zone 1 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1656">
                <div className="flex items-center" data-id="element-1657">
                  <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: '#b8b55c'
                }} data-id="element-1658" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1659">Zone 1</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1660">+$40</span>
              </div>

              {/* Zone 2 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1661">
                <div className="flex items-center" data-id="element-1662">
                  <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: '#e18251'
                }} data-id="element-1663" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1664">Zone 2</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1665">+$75</span>
              </div>

              {/* Zone 3 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1666">
                <div className="flex items-center" data-id="element-1667">
                  <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: '#ba8989'
                }} data-id="element-1668" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1669">Zone 3</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1670">
                  +$120
                </span>
              </div>

              {/* Zone 4 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1671">
                <div className="flex items-center" data-id="element-1672">
                  <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: '#c98ea3'
                }} data-id="element-1673" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1674">Zone 4</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1675">
                  +$150
                </span>
              </div>

              {/* Zone 5 */}
              <div className="flex items-center justify-between py-2.5 px-3 rounded-lg hover:bg-white/5 transition-colors" data-id="element-1676">
                <div className="flex items-center" data-id="element-1677">
                  <div className="w-2.5 h-2.5 rounded-full mr-3" style={{
                  backgroundColor: '#804747'
                }} data-id="element-1678" />
                  <p className="font-medium text-stone-200 text-sm" data-id="element-1679">Zone 5</p>
                </div>
                <span className="text-stone-300 text-xs font-medium" data-id="element-1680">
                  +$180
                </span>
              </div>
            </div>

            <div className="mt-4 pt-4 border-t border-white/10 space-y-3" data-id="element-1681">
              <div className="flex items-start" data-id="element-1682">
                <Info className="w-4 h-4 text-brand-orange mr-2 shrink-0 mt-0.5" data-id="element-1683" />
                <div data-id="element-1684">
                  <p className="text-xs text-stone-300" data-id="element-1685">
                    Outside Zone 5? We can still deliver. Call{' '}
                    <a href="tel:206-619-3742" className="text-brand-orange font-bold hover:underline" data-id="element-1686">
                      (206) 619-3742
                    </a>{' '}
                    for a custom quote.
                  </p>
                </div>
              </div>
              <p className="text-[11px] text-stone-400 italic" data-id="element-1687">
                *We do not deliver to Vashon, Maury, or Fox Island
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </main>;
}